import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError , toastError} from "../../lib/api";
import { PageHeader, Pill, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Contact2, Plus, AlertTriangle, Archive, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasActionAccess } from "../../lib/workspaces";
import { useTerminology } from "../../context/TerminologyContext";

const LIFECYCLES = ["prospect", "active_lead", "qualified_lead", "client", "referral_source", "partner", "inactive", "disqualified"];
const PROFESSIONAL_TITLES = ["Dr.", "NP", "Other"];
const SPECIALIZATIONS = ["General Practitioner / Family Medicine", "Respirology", "Cardiology", "ENT / Otolaryngology", "Rehabilitation Medicine", "Internal Medicine", "Pediatrics", "Psychiatry", "Neurology", "Dentistry", "Pharmacy", "Other"];
const EMPTY_FORM = { first_name: "", last_name: "", professional_title: "", specializations: [] as string[], email: "", phone: "", location: "", source: "", lifecycle_status: "prospect" };

export default function Contacts() {
  const { user } = useAuth();
  const canAdd = hasActionAccess(user, "contacts", "add");
  const canArchive = hasActionAccess(user, "contacts", "archive");
  const canRestore = hasActionAccess(user, "contacts", "restore");
  const { term } = useTerminology();
  const contact = term("contact");
  const contacts = term("contact", "plural");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [lifecycle, setLifecycle] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [leadSources, setLeadSources] = useState<any[]>([]);
  const [dupes, setDupes] = useState<any[]>([]);
  const [comparingDupe, setComparingDupe] = useState<any>(null);
  const [overrideCandidateId, setOverrideCandidateId] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params: any = { limit: 100, archived: showArchived };
      if (q) params.q = q;
      if (lifecycle) params.lifecycle = lifecycle;
      const { data } = await api.get("/crm/contacts", { params });
      setRows(data);
    } catch (e) { toastError(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived, lifecycle]);

  useEffect(() => {
    Promise.all([
      api.get("/tenant/workflow-requirements"),
      api.get("/config/lead_sources"),
    ]).then(([requirements, sources]) => {
      setRequiredFields(requirements.data?.required_fields?.contact || []);
      setLeadSources(sources.data || []);
    }).catch((e) => toastError(e));
  }, []);


  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => {
    if (!open) return;
    if (!form.first_name && !form.email && !form.phone) { setDupes([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/crm/contacts/duplicate-check", form);
        setDupes(data.suggestions || []);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [form, open]);

  const create = async (duplicateOverrideReason = "", duplicateCandidateId = "") => {
    setBusy(true);
    try {
      await api.post("/crm/contacts", { ...form, duplicate_check_completed: true, duplicate_override_reason: duplicateOverrideReason || null, duplicate_candidate_id: duplicateCandidateId || null });
      const incomplete = !form.email || !form.phone || !form.location || !form.source;
      toast.success(incomplete ? `${contact} created — profile incomplete` : `${contact} created`);
      setOpen(false); setDupes([]); setOverrideCandidateId(""); setOverrideReason("");
      setForm(EMPTY_FORM);
      // A successful create must remain visible. Clear stale list filters before
      // refreshing so users do not mistake a filtered-out row for a failed save.
      setQ("");
      setLifecycle("");
      setShowArchived(false);
      const { data: refreshed } = await api.get("/crm/contacts", { params: { limit: 100, archived: false } });
      setRows(refreshed);
    } catch (e) { toastError(e); }
    finally { setBusy(false); }
  };

  const archive = async (id, archived) => {
    try {
      await api.post(`/crm/contacts/${id}/${archived ? "restore" : "archive"}`);
      toast.success(archived ? "Restored" : "Archived");
      load();
    } catch (e) { toastError(e); }
  };

  return (
    <>
      <PageHeader title={contacts} subtitle="Lifecycle, dispositions, and duplicate suggestions"
        actions={canAdd ? (
          <Dialog open={open} onOpenChange={(next) => {
            if (!next && open && !busy && JSON.stringify(form) !== JSON.stringify(EMPTY_FORM) && !window.confirm("Discard unsaved contact information?")) return;
            setOpen(next);
          }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-contact-btn"><Plus className="w-4 h-4 mr-1" /> New {contact.toLowerCase()}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-contact-dialog">
              <DialogHeader><DialogTitle>New {contact.toLowerCase()}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name{requiredFields.includes("first_name") ? " *" : ""}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="contact-first-name" /></div>
                <div><Label>Last name{requiredFields.includes("last_name") ? " *" : ""}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="contact-last-name" /></div>
                <div><Label>Professional title *</Label><Select value={form.professional_title} onValueChange={(v) => setForm({ ...form, professional_title: v })}><SelectTrigger data-testid="contact-professional-title"><SelectValue placeholder="Dr., NP, or Other" /></SelectTrigger><SelectContent>{PROFESSIONAL_TITLES.map((title) => <SelectItem key={title} value={title}>{title}</SelectItem>)}</SelectContent></Select></div>
                <div className="col-span-2"><Label>Specialization *</Label><div className="mt-1 grid grid-cols-2 gap-2 rounded-md border border-slate-200 p-3">{SPECIALIZATIONS.map((specialization) => <label key={specialization} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.specializations.includes(specialization)} onChange={(e) => setForm({ ...form, specializations: e.target.checked ? [...form.specializations, specialization] : form.specializations.filter((item) => item !== specialization) })} />{specialization}</label>)}</div></div>
                <div><Label>Email{requiredFields.includes("email") ? " *" : ""}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /><p className="mt-1 text-xs text-slate-500">If unknown, leave blank. Do not reuse a placeholder email across contacts.</p></div>
                <div><Label>Phone{requiredFields.includes("phone") ? " *" : ""}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" /></div>
                <div><Label>Location{requiredFields.includes("location") ? " *" : ""}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="contact-location" /></div>
                <div><Label>Source{requiredFields.includes("source") ? " *" : ""}</Label>
                  {leadSources.length ? (
                    <Select value={form.source || undefined} onValueChange={(v) => setForm({ ...form, source: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="contact-source" aria-required={requiredFields.includes("source")}><SelectValue placeholder={requiredFields.includes("source") ? "Choose source (required)" : "Choose source"} /></SelectTrigger>
                      <SelectContent>
                        {!requiredFields.includes("source") && <SelectItem value="none">— none —</SelectItem>}
                        {leadSources.map((source) => <SelectItem key={source.code || source.id} value={source.code || source.id}>{source.label || source.code}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Enter source" data-testid="contact-source" />
                  )}
                  {requiredFields.includes("source") && <p className="mt-1 text-xs text-slate-500" data-testid="contact-source-required-help">Required by your organization’s workflow settings.</p>}
                </div>
                <div><Label>Lifecycle</Label>
                  <Select value={form.lifecycle_status} onValueChange={(v) => setForm({ ...form, lifecycle_status: v })}>
                    <SelectTrigger data-testid="contact-lifecycle"><SelectValue /></SelectTrigger>
                    <SelectContent>{LIFECYCLES.map((l) => <SelectItem key={l} value={l}>{l.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {dupes.length > 0 && (
                <div className="mt-2 border border-amber-200 bg-amber-50 p-3 rounded-md" data-testid="duplicate-suggestions">
                  <div className="text-xs font-semibold uppercase tracking-widest text-amber-800 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Possible duplicates ({dupes.length})
                  </div>
                  <div className="space-y-1.5 mt-2">
                    {dupes.map((d) => (
                      <div key={d.id} className="rounded border border-amber-200 bg-white p-2 text-sm" data-testid={`dupe-${d.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2"><div><span className="font-medium">{d.first_name} {d.last_name}</span><span className="ml-2 text-slate-500">{d.email || d.phone}</span></div><span className="rounded bg-amber-100 px-2 py-0.5 font-mono text-xs text-amber-800">{d.score}% · {d.reasons.join(", ")}</span></div>
                        {comparingDupe?.id===d.id&&<div className="mt-2 grid grid-cols-2 gap-2 rounded bg-slate-50 p-2 text-xs"><b>New entry</b><b>Existing record</b><span>{form.first_name} {form.last_name}<br/>{form.email||"—"}<br/>{form.phone||"—"}</span><span>{d.first_name} {d.last_name}<br/>{d.email||"—"}<br/>{d.phone||"—"}</span></div>}
                        <div className="mt-2 flex flex-wrap gap-2"><Button size="sm" variant="outline" onClick={()=>{window.location.href=`/contacts/${d.id}`;}}>Open existing record</Button><Button size="sm" variant="outline" onClick={()=>{toast.success("Using the existing contact; no duplicate was created.");setOpen(false);window.location.href=`/contacts/${d.id}`;}}>Link to existing record</Button><Button size="sm" variant="outline" onClick={()=>setComparingDupe(comparingDupe?.id===d.id?null:d)}>Compare records</Button><Button size="sm" onClick={()=>{setOverrideCandidateId(d.id);setOverrideReason("");}}>Save as separate anyway</Button></div>
                        {overrideCandidateId===d.id&&<div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3"><Label htmlFor={`contact-override-${d.id}`}>Reason for keeping a separate contact</Label><Textarea id={`contact-override-${d.id}`} className="mt-1 bg-white" value={overrideReason} onChange={(event)=>setOverrideReason(event.target.value)} placeholder="Example: Different doctor who shares the clinic reception email and phone." maxLength={1000}/><p className="mt-1 text-xs text-slate-600">This decision will be recorded with your name and date/time. Manager approval is not required.</p><div className="mt-2 flex justify-end gap-2"><Button size="sm" variant="outline" onClick={()=>{setOverrideCandidateId("");setOverrideReason("");}}>Cancel</Button><Button size="sm" disabled={!overrideReason.trim()||busy} onClick={()=>void create(overrideReason.trim(),d.id)}>{busy?"Saving…":"Confirm separate record"}</Button></div></div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 border-t border-slate-200 bg-white px-6 py-4">
                <Button
                  onClick={() => void (overrideCandidateId
                    ? create(overrideReason.trim(), overrideCandidateId)
                    : create())}
                  disabled={busy || (dupes.length > 0 && (!overrideCandidateId || !overrideReason.trim())) || !form.first_name.trim() || !form.last_name.trim() || !form.professional_title || form.specializations.length === 0 || requiredFields.some((field) => !String((form as any)[field] || "").trim())}
                  data-testid="contact-submit"
                >
                  {busy ? "Saving…" : overrideCandidateId ? "Confirm and save separate contact" : dupes.length ? "Select a duplicate option above" : "Create contact"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      />
      <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-4">
        {!canAdd && !canArchive && !canRestore && <div className="rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-600">View only — contact changes are disabled for your account.</div>}
        <div className="flex gap-2 items-center">
          <Input placeholder="Search name, phone, email, location, specialization…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="contact-search" />
          <Select value={lifecycle || "all"} onValueChange={(v) => setLifecycle(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="lifecycle-filter"><SelectValue placeholder="All lifecycles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lifecycles</SelectItem>
              {LIFECYCLES.map((l) => <SelectItem key={l} value={l}>{l.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="text-sm text-slate-600 flex items-center gap-1 ml-auto">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" /> Archived
          </label>
        </div>
        {rows.length === 0 ? (
          <EmptyState icon={Contact2} title={`No ${contacts.toLowerCase()}`} description={`Create your first ${contact.toLowerCase()}.`} />
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium">Email</th>
                  <th className="text-left px-4 py-2.5 font-medium">Lifecycle</th>
                  <th className="text-left px-4 py-2.5 font-medium">Source</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50/60" data-testid={`contact-row-${c.id.slice(0,8)}`}>
                    <td className="px-4 py-2.5 font-medium">{c.first_name} {c.last_name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{c.email || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2.5"><Pill value={c.lifecycle_status} type="lifecycle" /></td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{c.source || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-3">
                        <Link
                          to={`/contacts/${c.id}`}
                          className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
                          data-testid={`contact-open-${c.id.slice(0,8)}`}
                          aria-label={`Open contact ${c.first_name} ${c.last_name || ''}`.trim()}
                        >
                          Open details <ExternalLink className="w-3 h-3" />
                        </Link>
                        {((c.archived && canRestore) || (!c.archived && canArchive)) && (
                          <button className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1" onClick={() => archive(c.id, c.archived)} data-testid={`archive-${c.id.slice(0,8)}`}>
                            <Archive className="w-3.5 h-3.5" /> {c.archived ? "Restore" : "Archive"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
